import { use, useMemo, useState, useRef } from 'react';
import { useUrl } from 'crossroad';
import { useHistoricalEventsByPassageData } from '@/data/historical-events/historical-events-by-passage-data-context';
import { HistoricalEventsByBookDataContext } from '@/data/historical-events/historical-events-by-book-data-context';
import { HistoricalEventsDataContext } from '@/data/historical-events/historical-events-data-context';
import { PassageDataContext } from '@/data/passage/passage-data-context';
import { PlacesByPassageDataContext } from '@/data/places/places-by-passage-data-context';
import { Skeleton } from '@/components/ui/skeleton';
import { Calendar } from 'lucide-react';
import { PassageDateDisplay } from '../map/PassageDateDisplay';
import { yearToPixel, type TimelineEvent, MIN_YEAR, MAX_YEAR } from './timeline-utils';
import { TimelineControls, type RelevanceMode } from './TimelineControls';
import { TimelineEventModal } from './TimelineEventModal';
import { filterTimelineEvents } from './timeline-filtering';
import { useTimelineZoom } from './useTimelineZoom';
import { useTimelineVirtualization } from './useTimelineVirtualization';
import { useTimelineInitialPosition } from './useTimelineInitialPosition';
import { useTimelineSwimLanes } from './useTimelineSwimLanes';
import { TimelineSwimLanes, TimelineVerticalTicks, TimelineYearAxis } from './TimelineRenderer';
import { useDarkMode } from '@/lib/hooks/use-dark-mode';
import { useUserSettingsContext } from '@/lib/context/user-settings-context';
import { resolveDisplayDate } from '@/lib/date-utils';
import { ContextHero } from '../../context/ContextHero';
import timelineHero from '../../../assets/heroes/ancient_writing_16_9_v4_1769312000000.png';


interface Props {
    passageId: string;
    isTransitionAnimating?: boolean;
}

export default function PassageTimeline({ passageId, isTransitionAnimating }: Props) {
    const { data: historicalEventsByPassage, isLoading: eventsLoading } = useHistoricalEventsByPassageData(passageId);
    const historicalEventsByBookContext = use(HistoricalEventsByBookDataContext);
    const historicalEventsContext = use(HistoricalEventsDataContext);
    const passageContext = use(PassageDataContext);
    const placesByPassageContext = use(PlacesByPassageDataContext);

    const passage = useMemo(() => {
        return passageContext.data?.items.find(p => p.id === passageId);
    }, [passageContext.data, passageId]);

    const historicalEventsByBook = useMemo(() => {
        if (!passage || !historicalEventsByBookContext) return null;
        return historicalEventsByBookContext.getHistoricalEventsByBookByBook(passage.book);
    }, [historicalEventsByBookContext, passage]);

    const placesByPassage = useMemo(() => {
        if (!placesByPassageContext) return null;
        return placesByPassageContext.getPlacesByPassageByPassageId(passageId);
    }, [placesByPassageContext, passageId]);

    if (eventsLoading || !passageContext.data || !historicalEventsByPassage) {
        return <LoadingSkeleton />;
    }

    if (!historicalEventsByPassage.events?.length && !historicalEventsByBook?.events?.length) {
        return <EmptyState />;
    }

    return (
        <PassageTimelineInner
            passageId={passageId}
            passage={passage}
            historicalEventsByPassage={historicalEventsByPassage}
            historicalEventsByBook={historicalEventsByBook}
            historicalEvents={historicalEventsContext?.historicalEvents || []}
            placesByPassage={placesByPassage}
            isTransitionAnimating={isTransitionAnimating}
        />
    );
}

interface PassageTimelineInnerProps {
    passageId: string;
    passage: Passage | undefined;
    historicalEventsByPassage: HistoricalEventsByPassage;
    historicalEventsByBook: HistoricalEventsByBook | null;
    historicalEvents: HistoricalEvent[];
    placesByPassage: PlacesByPassage | null;
    isTransitionAnimating?: boolean;
}

function PassageTimelineInner({
    passageId,
    passage,
    historicalEventsByPassage,
    historicalEventsByBook,
    historicalEvents,
    isTransitionAnimating
}: PassageTimelineInnerProps) {
    const [, setUrl] = useUrl();
    // Dark mode
    const isDarkMode = useDarkMode();

    // User Settings
    const { settings } = useUserSettingsContext();
    const preferredChronology = settings?.preferredChronology;


    // State - Relevance Mode
    const [relevanceMode, setRelevanceMode] = useState<RelevanceMode>('passage');

    // State - World Events Filter
    const [showWorldEvents, setShowWorldEvents] = useState(false);

    // State - UI
    const [selectedEvent, setSelectedEvent] = useState<TimelineEvent | null>(null);

    // Refs
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const timelineContainerRef = useRef<HTMLDivElement>(null);
    const shouldCenterOnPassageRef = useRef(true); // Only center initially and on "Reset"

    // Custom hooks for zoom, virtualization, and initial positioning
    const {
        pixelsPerYear,
        setPixelsPerYear,
        timelineWidth,
        onControlZoomIn,
        onControlZoomOut
    } = useTimelineZoom({
        minYear: MIN_YEAR,
        maxYear: MAX_YEAR,
        timelineContainerRef,
        shouldCenterOnPassageRef
    });

    const { visibleStartYear, visibleEndYear } = useTimelineVirtualization({
        scrollContainerRef,
        minYear: MIN_YEAR,
        maxYear: MAX_YEAR,
        timelineWidth
    });

    // Resolve passage date based on preferred chronology
    const { activeDate: passageDate, alternatives: alternativeDates } = useMemo(() => {
        if (!historicalEventsByPassage.passageDate) return { activeDate: undefined, alternatives: undefined };
        return resolveDisplayDate(
            historicalEventsByPassage.passageDate,
            historicalEventsByPassage.alternativeDates,
            preferredChronology
        );
    }, [historicalEventsByPassage.passageDate, historicalEventsByPassage.alternativeDates, preferredChronology]);

    const { handleFitToPassage } = useTimelineInitialPosition({
        passageId,
        passageDate,
        minYear: MIN_YEAR,
        maxYear: MAX_YEAR,
        timelineWidth,
        pixelsPerYear,
        setPixelsPerYear,
        scrollContainerRef,
        shouldCenterOnPassageRef
    });


    // Filter all events first (without lane visibility filtering)
    // The swimlane hook will handle visibility filtering for rendering
    const allTimelineEvents = useMemo(() => {
        return filterTimelineEvents({
            passageEvents: historicalEventsByPassage.events,
            bookEvents: historicalEventsByBook?.events,
            referenceEvents: historicalEvents,
            relevanceMode,
            passage,
            isLaneVisible: () => true, // Include all events, swimlane visibility is handled by the hook
            historicalEventsByBook,
            preferredChronology,
            showWorldEvents
        });
    }, [
        historicalEventsByPassage.events,
        historicalEventsByBook?.events,
        historicalEventsByBook,
        historicalEvents,
        relevanceMode,
        passage,
        preferredChronology,
        showWorldEvents
    ]);


    // Swimlane management hook
    const {
        visibleSwimLanes,
        handleSwimLaneToggle,
        swimLanes,
        eventsByLane
    } = useTimelineSwimLanes({
        allTimelineEvents
    });

    return (
        <div className="relative h-full overflow-hidden flex flex-col">
            <ContextHero
                eyebrow="HISTORICAL CONTEXT"
                headline="Timeline of the Ancient World"
                subtitle="Discover how this passage fits into God's story and global history. Toggle 'Explore More Events' to see contemporaneous world history and witness how God's story intersects with the nations."
                image={timelineHero}
                backgroundOffset="50% 50%"
                onClose={() => setUrl(`/app/passage/${passage?.id}`)}
                showClose={false}
                variant="slim"
                className="mb-0 rounded-b-none"
            >
                {/* Timeline controls integrated into hero - moved back to children for natural growth */}
                <div className="relative z-30 pt-2 pb-1">
                    <TimelineControls
                        relevanceMode={relevanceMode}
                        onRelevanceModeChange={setRelevanceMode}
                        showWorldEvents={showWorldEvents}
                        onShowWorldEventsChange={setShowWorldEvents}
                        onZoomIn={onControlZoomIn}
                        onZoomOut={onControlZoomOut}
                        onFitToPassage={handleFitToPassage}
                    />
                </div>
            </ContextHero>

            {/* Timeline container */}
            <div
                ref={(el) => {
                    scrollContainerRef.current = el;
                    timelineContainerRef.current = el;
                }}
                className="relative overflow-x-auto overflow-y-auto flex-1"
            >
                {/* Swim lanes */}
                <div className="relative min-w-full flex flex-col min-h-full" style={{ width: `${timelineWidth}px`, transition: 'none' }}>
                    <style>{`
                        @media (min-width: 640px) {
                            .timeline-container { --lane-header-width: 232px; }
                        }
                        @media (max-width: 639px) {
                            .timeline-container { --lane-header-width: 60px; }
                        }
                    `}</style>

                    {/* Passage date vertical band (behind lanes) */}
                    {passageDate && (
                        <div
                            className="absolute top-0 bottom-0 bg-blue-500/10 border-l-2 border-r-2 border-blue-500/30 pointer-events-none z-0 sm:left-[232px] left-[60px]"
                            style={{
                                transform: `translateX(${yearToPixel(
                                    passageDate.startYear,
                                    MIN_YEAR,
                                    MAX_YEAR,
                                    timelineWidth
                                )}px)`,
                                width: `${yearToPixel(
                                    passageDate.endYear || passageDate.startYear,
                                    MIN_YEAR,
                                    MAX_YEAR,
                                    timelineWidth
                                ) - yearToPixel(
                                    passageDate.startYear,
                                    MIN_YEAR,
                                    MAX_YEAR,
                                    timelineWidth
                                )}px`
                            }}
                        />
                    )}


                    <TimelineSwimLanes
                        swimLanes={swimLanes}
                        eventsByLane={eventsByLane}
                        visibleSwimLanes={visibleSwimLanes}
                        handleSwimLaneToggle={handleSwimLaneToggle}
                        timelineWidth={timelineWidth}
                        visibleStartYear={visibleStartYear}
                        visibleEndYear={visibleEndYear}
                        onEventClick={setSelectedEvent}
                    />

                    <TimelineVerticalTicks
                        timelineWidth={timelineWidth}
                        visibleStartYear={visibleStartYear}
                        visibleEndYear={visibleEndYear}
                    />

                    <TimelineYearAxis
                        timelineWidth={timelineWidth}
                        visibleStartYear={visibleStartYear}
                        visibleEndYear={visibleEndYear}
                    >
                        {/* Passage date display - inside axis so it scrolls horizontally with it */}
                        {passageDate && (
                            <div
                                id="passage-timeline-target"
                                className="absolute -bottom-0.5 z-50 pointer-events-auto translate-y-[6px]"
                                style={{
                                    left: `${yearToPixel(
                                        passageDate.startYear,
                                        MIN_YEAR,
                                        MAX_YEAR,
                                        timelineWidth
                                    )}px`,
                                    transform: 'translateX(-50%)',
                                    transition: 'none'
                                }}
                            >
                                <PassageDateDisplay
                                    passageDate={passageDate}
                                    alternativeDates={alternativeDates}
                                    showThrob={isTransitionAnimating}
                                />
                            </div>
                        )}
                    </TimelineYearAxis>
                </div>
            </div>

            {/* Event modal */}
            <TimelineEventModal
                event={selectedEvent}
                onClose={() => setSelectedEvent(null)}
            />
        </div>
    );
}

function LoadingSkeleton() {
    return (
        <div className="space-y-4 p-4">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-24 w-full" />
        </div>
    );
}

function EmptyState() {
    return (
        <div className="flex flex-col items-center justify-center p-8 text-center h-full">
            <Calendar className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Historical Events Available</h3>
            <p className="text-sm text-muted-foreground max-w-md">
                Historical events information for this passage has not been generated yet.
            </p>
        </div>
    );
}

